import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/fp/isEmpty';
import isUndefined from 'lodash/fp/isUndefined';
import { STORE_BASE_PATH } from '../../common/constants';
import { clearRouteAndMarkers } from '../../features/livemonitorCommunication/MapRouteRendering';
import {
    type Coordinates,
    facadeApiV3 as facadeApi,
    type ItineraryResponse,
    ItineraryStatus,
    type TourResponse,
} from '../facade/facadeApiV3';

export const getCoordinatesFromItinerary = (itinerary: ItineraryResponse): Coordinates[] => {
    const sections = itinerary.sections;
    if (isUndefined(sections)) {
        return [];
    }

    return sections.flatMap(({ spans }) => {
        if (isUndefined(spans)) {
            return [];
        }
        return spans.flatMap(({ coordinates }) => {
            if (isUndefined(coordinates)) {
                return [];
            }
            return coordinates;
        });
    });
};

export type Itinerary = ItineraryResponse & { drivingCoordinates: Coordinates[] };

export interface SettingsSnapshot {
    vehicleProfile: string;
    search: string;
    charging: string;
    route: string;
}

export interface TourState {
    tourId?: string;
    tourItineraryId?: string;
    fetchTourPending: boolean;
    itineraries?: Itinerary[];
    selectedItinerary?: number;
    tourHandlingItineraries?: Itinerary[];
    tourHandlingSelectedItinerary?: number;
    currentTour?: TourResponse;
    pendingTour?: TourResponse;
    pendingTourId?: string;
    settingsSnapshot?: SettingsSnapshot;
}

const initialState: TourState = {
    fetchTourPending: false,
};

export const tourSlice = createSlice({
    name: `${STORE_BASE_PATH}tour`,
    initialState,
    reducers: {
        updateTourId: (state: TourState, action: PayloadAction<string | undefined>) => {
            state.tourId = action.payload;
        },
        updateTourItineraryId: (state: TourState, action: PayloadAction<string | undefined>) => {
            state.tourItineraryId = action.payload;
        },
        updateSelectedItinerary: (state: TourState, action: PayloadAction<number | undefined>) => {
            state.selectedItinerary = action.payload;
        },
        removeItineraries: (state: TourState) => {
            state.selectedItinerary = undefined;
            state.currentTour = undefined;
            state.itineraries = undefined;
            state.tourItineraryId = undefined;
        },
        /**
         * Seeds the planning-flow itineraries + selectedItinerary from the tour-handling
         * itineraries so that RouteSummaryPage and FinishButton are immediately usable
         * after editTour without requiring a fresh calculation.
         */
        seedItinerariesFromHandling: (state: TourState) => {
            if (state.tourHandlingItineraries && state.tourHandlingItineraries.length > 0) {
                state.itineraries = state.tourHandlingItineraries;
                state.selectedItinerary = state.tourHandlingSelectedItinerary ?? 0;
                state.tourItineraryId = state.tourHandlingItineraries.at(
                    state.tourHandlingSelectedItinerary ?? 0
                )?.itineraryId;
            }
        },
        saveSettingsSnapshot: (state: TourState, action: PayloadAction<SettingsSnapshot>) => {
            state.settingsSnapshot = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addMatcher(
                isAnyOf(facadeApi.endpoints.createTour.matchPending, facadeApi.endpoints.updateTour.matchPending),
                state => {
                    state.fetchTourPending = true;
                }
            )
            .addMatcher(
                isAnyOf(facadeApi.endpoints.createTour.matchFulfilled, facadeApi.endpoints.updateTour.matchFulfilled),
                (state, action) => {
                    state.fetchTourPending = false;
                    state.pendingTourId = action.payload.tour?.tourId;
                    state.pendingTour = action.payload.tour;

                    // sync itineraries
                    if (state.pendingTour) {
                        state.currentTour = state.pendingTour;
                        state.pendingTour = undefined;
                    }
                    if (state.pendingTourId) {
                        state.tourId = state.pendingTourId;
                        state.pendingTourId = undefined;
                    }

                    const itineraries = action.payload.itineraries?.itineraries;

                    if (isEmpty(itineraries)) {
                        state.selectedItinerary = undefined;
                        state.currentTour = undefined;
                        state.itineraries = undefined;
                        state.tourItineraryId = undefined;
                        clearRouteAndMarkers();
                    } else if (itineraries && itineraries?.length > 0) {
                        state.selectedItinerary = 0;
                        state.tourItineraryId = itineraries.at(0)?.itineraryId;
                        state.itineraries = itineraries?.slice(0, 3).map(itinerary => ({
                            ...itinerary,
                            drivingCoordinates: getCoordinatesFromItinerary(itinerary),
                        }));
                    }
                }
            )
            .addMatcher(facadeApi.endpoints.fetchTourItineraries.matchFulfilled, (state, action) => {
                state.fetchTourPending = false;
                const itineraries = action.payload.itineraries;

                if (isEmpty(itineraries)) {
                    state.tourHandlingSelectedItinerary = undefined;
                    state.tourHandlingItineraries = undefined;
                    state.tourItineraryId = undefined;
                } else if (itineraries && itineraries.length > 0) {
                    const sliced = itineraries.slice(0, 3);
                    const currentIndex = sliced.findIndex(it => it.status === ItineraryStatus.Current);
                    const selectedIndex = currentIndex >= 0 ? currentIndex : 0;

                    state.tourHandlingSelectedItinerary = selectedIndex;
                    state.tourItineraryId = sliced.at(selectedIndex)?.itineraryId;
                    state.tourHandlingItineraries = sliced.map(itinerary => ({
                        ...itinerary,
                        drivingCoordinates: getCoordinatesFromItinerary(itinerary),
                    }));
                }
            })
            .addMatcher(
                isAnyOf(
                    facadeApi.endpoints.createTour.matchRejected,
                    facadeApi.endpoints.updateTour.matchRejected,
                    facadeApi.endpoints.fetchTourItineraries.matchRejected
                ),
                state => {
                    state.fetchTourPending = false;
                }
            )
            .addMatcher(
                isAnyOf(
                    facadeApi.endpoints.createTour.matchPending,
                    facadeApi.endpoints.updateTour.matchPending,
                    facadeApi.endpoints.fetchTourItineraries.matchPending
                ),
                state => {
                    state.fetchTourPending = true;
                }
            )
            .addMatcher(facadeApi.endpoints.getTour.matchFulfilled, (state, action) => {
                state.currentTour = action.payload;
            })
            .addMatcher(facadeApi.endpoints.getTour.matchPending, (state, action) => {
                if (state.currentTour?.tourId !== action.meta.arg.originalArgs.id) {
                    state.currentTour = undefined;
                }
            });
    },
});

export const {
    updateTourId,
    updateTourItineraryId,
    updateSelectedItinerary,
    removeItineraries,
    saveSettingsSnapshot,
    seedItinerariesFromHandling,
} = tourSlice.actions;

export default tourSlice.reducer;
